import { useEffect, useState } from 'react';
import useApi from '../api';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import SchedulingFieldsModal from './SchedulingFieldsModal';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import SlackNotificationsModal from './actions/SlackNotificationsModal';
import AimdocNotificationsModal from './actions/AimdocNotificationsModal';
import SlackIcon from '../icons/slack_logo.webp';
import ChiliPiperIcon from '../icons/chili_piper_logo.png';
import CalendlyIcon from '../icons/calendly_logo.png';
import TidyCalIcon from '../icons/tidycal_logo.jpeg';
import GoogleAdsIcon from '../icons/google_ads_logo.png';
import { Trash2, Pencil, Loader2, Lock, Zap, ExternalLink } from 'lucide-react';
import HubspotIcon from '../icons/hubspot_logo.webp';
import SalesforceIcon from '../icons/salesforce_logo.png';
import HubspotFieldsModal from './actions/HubspotFieldsModal';
import SalesforceFieldsModal from './actions/SalesforceFieldsModal';
import AttioFieldsModal from './actions/AttioFieldsModal';
import { toast } from 'react-toastify';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logo from '../../logo_symbol.png'
import { useUser } from '../user/UserContext';
import DefaultIcon from '../icons/default_logo.jpeg';
import RevenueHeroIcon from '../icons/revenue_hero_logo.jpeg';
import CalComIcon from '../icons/cal_logo.webp';
import AttioIcon from '../icons/attio_logo.png';
import AgentTabHeader from './tabs/AgentTabHeader';
import { formatCalendarOptionLabel } from '../../lib/calendarIntegrationLabel';

interface Integration {
    id: string;
    name: string;
    description: string;
    details: any;
    type: string;
    user_id?: string | null;
}

interface CrmIntegration extends Integration {
    details: {
        integration: string;
        type: string;
        token: string;
        refresh_token: string;
        portal_id?: string;
        form_guid?: string;
    }
}

interface GoogleAdsAccount {
    id: string;
    resource_name: string;
    name: string;
}

interface GoogleAdsConversionAction {
    id: string;
    name: string;
}

interface ActionsProps {
    actions: Record<string, any>;
    setActions: (actions: Record<string, any>) => void;
    setNeedsUpdate: (needsUpdate: boolean) => void;
    formFields: any[];
    agentId?: string;
    agentName: string;
    escalationEnabled: boolean;
    enableTracks: boolean;
}

const LoadingSkeleton = () => (
    <div className="mt-6 space-y-4">
        {[1].map((i) => (
            <div key={i} className="p-4 rounded-lg border">
                <div className="animate-pulse flex items-center justify-between">
                    <div className="space-y-2">
                        <div className="h-5 rounded w-32"></div>
                        <div className="h-4 rounded w-48"></div>
                    </div>
                    <div className="w-6 h-6 rounded-full"></div>
                </div>
            </div>
        ))}
    </div>
);

interface IntegrationCardConfig {
    name: string;
    displayName: string;
    icon: string;
    iconClassName?: string;
}

const INTEGRATIONS_REQUIRING_MAPPING = ['chili-piper', 'default', 'revenue-hero'];

const CRM_CARD_CONFIGS: IntegrationCardConfig[] = [
    { name: 'hubspot', displayName: 'HubSpot', icon: HubspotIcon },
    { name: 'salesforce', displayName: 'Salesforce', icon: SalesforceIcon, iconClassName: 'h-4' },
    { name: 'attio', displayName: 'Attio', icon: AttioIcon, iconClassName: 'h-5' }
];

const SCHEDULING_CARD_CONFIGS: IntegrationCardConfig[] = [
    { name: 'calendly', displayName: 'Calendly', icon: CalendlyIcon },
    { name: 'tidycal', displayName: 'TidyCal', icon: TidyCalIcon },
    { name: 'cal-com', displayName: 'Cal.com', icon: CalComIcon },
    { name: 'hubspot-meetings', displayName: 'HubSpot Meetings', icon: HubspotIcon },
    { name: 'chili-piper', displayName: 'Chili Piper', icon: ChiliPiperIcon },
    { name: 'default', displayName: 'Default', icon: DefaultIcon },
    { name: 'revenue-hero', displayName: 'RevenueHero', icon: RevenueHeroIcon }
];

const ADS_CARD_CONFIGS: IntegrationCardConfig[] = [
    { name: 'google-ads', displayName: 'Google Ads', icon: GoogleAdsIcon }
];

export default function Actions({ actions, setActions, setNeedsUpdate, formFields, agentId, agentName, escalationEnabled, enableTracks }: ActionsProps) {
    const { get, post } = useApi();
    const { checkLicenseType } = useUser();
    const [calendarIntegrations, setCalendarIntegrations] = useState<Integration[]>([]);
    const [organizationUsers, setOrganizationUsers] = useState<any[]>([]);
    const [selectedCalendarIntegration, setSelectedCalendarIntegration] = useState<string | null>(
        actions.scheduling?.id || null
    );
    const [isLoading, setIsLoading] = useState(true);
    const [isFieldMappingOpen, setIsFieldMappingOpen] = useState(false);
    const [pendingIntegration, setPendingIntegration] = useState<Integration | null>(null);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [integrationToDisconnect, setIntegrationToDisconnect] = useState<Integration | null>(null);
    const [slackIntegration, setSlackIntegration] = useState<Integration | null>(null);
    const [isSlackModalOpen, setIsSlackModalOpen] = useState(false);
    const [selectedNotificationIntegration, setSelectedNotificationIntegration] = useState<Integration | null>(null);
    const [showSlackDisconnectModal, setShowSlackDisconnectModal] = useState(false);
    const [crmIntegrations, setCrmIntegrations] = useState<CrmIntegration[]>([]);
    const [isHubspotModalOpen, setIsHubspotModalOpen] = useState(false);
    const [isSalesforceModalOpen, setIsSalesforceModalOpen] = useState(false);
    const [isAttioModalOpen, setIsAttioModalOpen] = useState(false);
    const [selectedCrmIntegration, setSelectedCrmIntegration] = useState<Integration | null>(null);
    const [googleAdsIntegration, setGoogleAdsIntegration] = useState<Integration | null>(null);
    const [isGoogleAdsModalOpen, setIsGoogleAdsModalOpen] = useState(false);
    const [googleAdsAccounts, setGoogleAdsAccounts] = useState<GoogleAdsAccount[]>([]);
    const [isLoadingAdsAccounts, setIsLoadingAdsAccounts] = useState(false);
    const [selectedGoogleAdsAccount, setSelectedGoogleAdsAccount] = useState<string | undefined>(
        actions.ads?.customer_id
    );
    const [googleAdsError, setGoogleAdsError] = useState<string | null>(null);
    const [isSavingGoogleAdsConfig, setIsSavingGoogleAdsConfig] = useState(false);
    const [googleAdsConversionValue, setGoogleAdsConversionValue] = useState<string>(
        actions.ads?.default_conversion_value?.toString() || "1.0"
    );
    const [isAimdocModalOpen, setIsAimdocModalOpen] = useState(false);
    const [selectedAimdocNotificationType, setSelectedAimdocNotificationType] = useState<'escalations' | 'buyer_intent'>('escalations');
    // Removed: assessment/report modals now live in Qualification tab

    useEffect(() => {
        const fetchIntegrations = async () => {
            try {
                // Use include_all_calendars=true to get all users' calendar integrations
                const [integrationsResponse, usersResponse] = await Promise.all([
                    get('/integrations/?include_all_calendars=true'),
                    get('/users/').catch(() => ({ data: [] })),
                ]);
                const integrations = integrationsResponse.data;
                setOrganizationUsers(usersResponse.data || []);

                const slackInt = integrations.find((int: Integration) => int.name === 'slack');
                if (slackInt) {
                    setSlackIntegration(slackInt);
                }

                const calendarApps = SCHEDULING_CARD_CONFIGS.map((config) => config.name);
                const filteredIntegrations = integrations.filter((integration: Integration) =>
                    calendarApps.includes(integration.name)
                );
                setCalendarIntegrations(filteredIntegrations);

                const crmApps = ['hubspot', 'salesforce', 'attio'];
                const filteredCrmIntegrations = integrations.filter((integration: Integration) =>
                    crmApps.includes(integration.name) && integration.details.type === 'crm'
                );
                setCrmIntegrations(filteredCrmIntegrations);
                
                const adsInt = integrations.find((int: Integration) => int.name === 'google-ads');
                setGoogleAdsIntegration(adsInt || null);
            } catch (error) {
                console.error('Error fetching integrations:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchIntegrations();
    }, []);

    // Deduplicate CRM configs on mount and remove orphaned configs
    useEffect(() => {
        if (actions.crm && Array.isArray(actions.crm) && actions.crm.length > 0 && crmIntegrations.length > 0) {
            const validIntegrationIds = new Set(crmIntegrations.map(int => int.id));
            const seen = new Set<string>();
            
            const cleanedCrms = actions.crm.filter((crm: any) => {
                // Remove configs that reference non-existent integrations
                if (!crm.id || !validIntegrationIds.has(crm.id)) {
                    console.warn(`Removing orphaned CRM config for ${crm.name || 'unknown'} with ID ${crm.id}`);
                    return false;
                }
                
                // Remove duplicate configs by name
                if (!crm.name || seen.has(crm.name)) {
                    return false;
                }
                seen.add(crm.name);
                return true;
            });

            // Only update if changes were made
            if (cleanedCrms.length !== actions.crm.length) {
                console.warn(`Cleaned up ${actions.crm.length - cleanedCrms.length} invalid/duplicate CRM config(s)`);
                setActions((prevActions: Record<string, any>) => ({
                    ...prevActions,
                    crm: cleanedCrms
                }));
                setNeedsUpdate(true);
            }
        }
    }, [actions.crm, crmIntegrations, setActions, setNeedsUpdate]); // Run when CRM integrations or CRM configs change

    useEffect(() => {
        // This useEffect was for fetching conversion actions. REMOVE IT.
        // const fetchConversionActions = async () => { 
        // if (!selectedGoogleAdsAccount) { 
        // setGoogleAdsConversionActions([]); 
        // setSelectedConversionActionId(undefined); 
        // return; 
        // } 
        // 
        // setIsLoadingConversionActions(true); 
        // setConversionActionsError(null); 
        // setGoogleAdsConversionActions([]); 
        // const currentConfigActionId = actions.ads?.customer_id === selectedGoogleAdsAccount ? actions.ads?.conversion_action_id : undefined; 
        // setSelectedConversionActionId(currentConfigActionId); 
        // 
        // try { 
        // const response = await get(`/integrations/google-ads/${selectedGoogleAdsAccount}/conversion-actions`); 
        // setGoogleAdsConversionActions(response.data || []); 
        // if (!response.data || response.data.length === 0) { 
        // setConversionActionsError("No suitable conversion actions found for this account. Please create one of type 'Import from clicks' in Google Ads."); 
        // } 
        // } catch (error: any) { 
        // console.error('Error fetching Google Ads conversion actions:', error); 
        // const errorMsg = error?.detail || 'Failed to load conversion actions.'; 
        // setConversionActionsError(errorMsg); 
        // toast.error(errorMsg); 
        // } finally { 
        // setIsLoadingConversionActions(false); 
        // } 
        // }; 
        // 
        // fetchConversionActions(); 
    }, [selectedGoogleAdsAccount]); // Keep dependency array for now, or remove if block is empty

    const handleCardClick = (integration: Integration) => {
        if (integration && INTEGRATIONS_REQUIRING_MAPPING.includes(integration.name)) {
            setPendingIntegration(integration);
            setIsFieldMappingOpen(true);
        } else {
            if (selectedCalendarIntegration !== integration.id) {
                setSelectedCalendarIntegration(integration.id);
                setActions({
                    ...actions,
                    scheduling: {
                        id: integration.id,
                        name: integration.name,
                        details: integration.details
                    }
                });
                setNeedsUpdate(true);
            }
        }
    };

    const handleDisconnect = () => {
        setSelectedCalendarIntegration(null);
        setActions({
            ...actions,
            scheduling: null
        });
        setNeedsUpdate(true);
        setShowDisconnectModal(false);
        setIntegrationToDisconnect(null);
    };


    const handleFieldMappingSave = (
        fieldMappings: Record<string, string>,
        valueMappings: Record<string, Record<string, string>> = {}
    ) => {
        if (pendingIntegration && Object.keys(fieldMappings).length > 0) {
            setSelectedCalendarIntegration(pendingIntegration.id);
            setActions({
                ...actions,
                scheduling: {
                    id: pendingIntegration.id,
                    name: pendingIntegration.name,
                    details: pendingIntegration.details,
                    fields: fieldMappings,
                    ...(Object.keys(valueMappings).length > 0 ? { value_mappings: valueMappings } : {})
                }
            });
            setNeedsUpdate(true);
        }
        setPendingIntegration(null);
    };

    const handleModalClose = () => {
        setPendingIntegration(null);
        setIsFieldMappingOpen(false);
    };

    const handleSlackDisconnect = () => {
        setActions({
            ...actions,
            slack: null
        });
        setNeedsUpdate(true);
        setShowSlackDisconnectModal(false);
    };

    const handleCrmIntegrationClick = (integration: Integration) => {
        setSelectedCrmIntegration(integration);
        if (integration.name === 'hubspot') {
            setIsHubspotModalOpen(true);
        } else if (integration.name === 'salesforce') {
            setIsSalesforceModalOpen(true);
        } else if (integration.name === 'attio') {
            setIsAttioModalOpen(true);
        }
    };

    const handleCrmMappingSave = (
        fieldMappings: Record<string, string>,
        defaultFields: Record<string, string>,
        fieldMetadata?: Record<string, any>,
        readFields?: any[],
        readDeals?: boolean,
        valueMappings?: Record<string, Record<string, string>>,
        aiTools?: string[],
        activityTypes?: string[]
    ) => {
        const existingCrms = actions.crm || [];
        const crmIndex = existingCrms.findIndex((crm: any) => crm.id === selectedCrmIntegration?.id);

        const updatedCrm = {
            id: selectedCrmIntegration?.id,
            name: selectedCrmIntegration?.name,
            fields: fieldMappings,
            default_fields: defaultFields,
            ...(fieldMetadata ? { field_metadata: fieldMetadata } : {}),
            ...(readFields !== undefined ? { read_fields: readFields } : {}),
            ...(readDeals !== undefined ? { read_deals: readDeals } : {}),
            ...(aiTools !== undefined ? { ai_tools: aiTools } : {}),
            ...(activityTypes !== undefined ? { activity_types: activityTypes } : {}),
            ...(valueMappings !== undefined ? { value_mappings: valueMappings } : {})
        };

        let updatedCrms;
        if (crmIndex >= 0) {
            // Replace existing CRM config with same integration ID
            updatedCrms = existingCrms.map((crm: any, index: number) => 
                index === crmIndex ? updatedCrm : crm
            );
        } else {
            // Add new CRM config, but first remove any configs with same integration name (prevent duplicates by name)
            const filteredCrms = existingCrms.filter((crm: any) => crm.name !== selectedCrmIntegration?.name);
            updatedCrms = [...filteredCrms, updatedCrm];
        }

        setActions({
            ...actions,
            crm: updatedCrms
        });
        setNeedsUpdate(true);
    };

    const handleSalesforceMappingSave = (salesforceConfig: any) => {
        const existingCrms = actions.crm || [];
        const crmIndex = existingCrms.findIndex((crm: any) => crm.id === selectedCrmIntegration?.id);

        const leadFields = salesforceConfig?.fields || salesforceConfig?.object_mappings?.lead?.fields || {};
        const leadDefaults = salesforceConfig?.default_fields || salesforceConfig?.object_mappings?.lead?.default_fields || {};

        const updatedCrm = {
            ...salesforceConfig,
            id: selectedCrmIntegration?.id,
            name: selectedCrmIntegration?.name,
            // Backwards-compatible lead keys (ensure present)
            fields: leadFields,
            default_fields: leadDefaults,
        };

        let updatedCrms;
        if (crmIndex >= 0) {
            updatedCrms = existingCrms.map((crm: any, index: number) =>
                index === crmIndex ? updatedCrm : crm
            );
        } else {
            const filteredCrms = existingCrms.filter((crm: any) => crm.name !== selectedCrmIntegration?.name);
            updatedCrms = [...filteredCrms, updatedCrm];
        }

        setActions({
            ...actions,
            crm: updatedCrms
        });
        setNeedsUpdate(true);
    };

    const handleRemoveCrmIntegration = (integration: Integration) => {
        const updatedCrms = actions.crm?.filter((crm: any) => crm.id !== integration.id) || [];
        setActions({
            ...actions,
            crm: updatedCrms
        });
        setNeedsUpdate(true);
    };

    const handleRemoveSlackConfig = (e: React.MouseEvent) => {
        e.stopPropagation();
        setActions({
            ...actions,
            slack: undefined
        });
        setNeedsUpdate(true);
    };

    const handleRemoveScheduling = (integration: Integration) => {
        setSelectedCalendarIntegration(null);
        setActions({
            ...actions,
            scheduling: undefined
        });
        setNeedsUpdate(true);
    };

    const handleConfigureGoogleAds = async () => {
        if (!googleAdsIntegration) return;
        
        setIsGoogleAdsModalOpen(true);
        setIsLoadingAdsAccounts(true);
        setGoogleAdsError(null);
        setGoogleAdsAccounts([]);
        // REMOVE setGoogleAdsConversionActions([]);
        // REMOVE setConversionActionsError(null);
        setSelectedGoogleAdsAccount(actions.ads?.customer_id);
        // REMOVE setSelectedConversionActionId(actions.ads?.conversion_action_id);
        setGoogleAdsConversionValue(actions.ads?.default_conversion_value?.toString() || "1.0");

        try {
            const response = await get('/integrations/google-ads/customers');
            const accounts = response.data || [];
            setGoogleAdsAccounts(accounts);
            
            if (accounts.length === 0) {
                setGoogleAdsError("No accessible Google Ads accounts found. Ensure the connected user has access.");
            }
        } catch (error: any) {
            console.error('Error fetching Google Ads accounts:', error);
            const errorMsg = error?.detail || 'Failed to load Google Ads accounts.';
            setGoogleAdsError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setIsLoadingAdsAccounts(false);
        }
    };

    const handleGoogleAdsAccountSelect = (customerId: string) => {
        if (customerId !== selectedGoogleAdsAccount) {
            setSelectedGoogleAdsAccount(customerId);
            // When account changes, clear any previously auto-selected/created action ID from actions object if desired,
            // or let the save overwrite it.
            // For now, we don't touch actions.ads here, only local selectedGoogleAdsAccount.
        }
    };

    const handleGoogleAdsSave = async () => {
        if (!googleAdsIntegration || !selectedGoogleAdsAccount) {
            toast.warn("Please select a Google Ads account.");
            return;
        }
        
        setIsSavingGoogleAdsConfig(true);
        setGoogleAdsError(null);

        try {
            const numericConversionValue = parseFloat(googleAdsConversionValue);
            if (isNaN(numericConversionValue) || numericConversionValue <= 0) {
                setGoogleAdsError("Conversion value must be a positive number.");
                setIsSavingGoogleAdsConfig(false);
                return;
            }

            const response = await post('/integrations/google-ads/ensure-conversion-action', {
                 customer_id: selectedGoogleAdsAccount,
                 integration_id: googleAdsIntegration.id,
                 default_conversion_value: numericConversionValue
            });

            if (response.data && response.data.conversion_action_id) {
                setActions({
                    ...actions,
                    ads: {
                        integration_id: googleAdsIntegration.id,
                        customer_id: selectedGoogleAdsAccount,
                        conversion_action_id: response.data.conversion_action_id,
                        default_conversion_value: numericConversionValue
                    }
                });
                setNeedsUpdate(true);
                setIsGoogleAdsModalOpen(false);
                toast.success("Google Ads configuration saved.");
            } else {
                throw new Error("Failed to get conversion action ID from server.");
            }
        } catch (error: any) {
            console.error('Error saving Google Ads config:', error);
            const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to save Google Ads configuration.';
            setGoogleAdsError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setIsSavingGoogleAdsConfig(false);
        }
    };

    const handleRemoveGoogleAds = () => {
        setActions({
            ...actions,
            ads: undefined
        });
        setSelectedGoogleAdsAccount(undefined);
        // REMOVE setSelectedConversionActionId(undefined);
        // REMOVE setGoogleAdsConversionActions([]);
        // REMOVE setConversionActionsError(null);
        setGoogleAdsConversionValue("1.0"); // Reset to default
        setNeedsUpdate(true);
    };

    const handleAimdocNotificationClick = (type: 'escalations' | 'buyer_intent') => {
        setSelectedAimdocNotificationType(type);
        setIsAimdocModalOpen(true);
    };

    const handleAimdocNotificationSave = (config: any) => {
        const actionKey = selectedAimdocNotificationType;
        setActions({
            ...actions,
            [actionKey]: config
        });
        setNeedsUpdate(true);
    };

    const handleRemoveAimdocNotification = (type: 'escalations' | 'buyer_intent') => {
        setActions({
            ...actions,
            [type]: undefined
        });
        setNeedsUpdate(true);
    };

    // Removed: assessment handlers now live in Qualification tab

    // Removed: report handlers now live in Qualification tab

    // Helper to get CRM status info
    const getCrmInfo = (integration: Integration) => {
        const configuredCrm = actions.crm?.find((crm: any) => crm.id === integration.id);
        const getSalesforceObjectKeys = (): Array<'lead' | 'contact' | 'account'> => {
            const isKey = (o: any): o is 'lead' | 'contact' | 'account' =>
                o === 'lead' || o === 'contact' || o === 'account';
            const raw = configuredCrm?.objects;
            if (Array.isArray(raw)) {
                const keys = raw
                    .filter((o: any) => typeof o === 'string')
                    .map((o: string) => o.trim().toLowerCase())
                    .filter(isKey);
                const seen = new Set<string>();
                const deduped = keys.filter((k) => (seen.has(k) ? false : (seen.add(k), true)));
                return (deduped.length > 0 ? deduped : ['lead']) as Array<'lead' | 'contact' | 'account'>;
            }
            return ['lead'];
        };

        const getMappedFieldCount = () => {
            if (!configuredCrm) return 0;
            if (integration.name === 'salesforce' && configuredCrm?.object_mappings) {
                const objectMappings = configuredCrm.object_mappings || {};
                const keys = getSalesforceObjectKeys();
                return keys.reduce((sum: number, key: 'lead' | 'contact' | 'account') => {
                    const fields = objectMappings?.[key]?.fields || {};
                    return sum + Object.keys(fields).length;
                }, 0);
            }
            const configuredFieldCount = Object.keys(configuredCrm.fields || {}).length;
            if (integration.name === 'attio') {
                const formFieldNames = new Set(
                    (formFields || [])
                        .map((field: any) => (typeof field?.name === 'string' ? field.name : ''))
                        .filter(Boolean)
                );
                const autoMappedCount = ['first_name', 'last_name', 'email'].filter((name) => formFieldNames.has(name)).length;
                return configuredFieldCount + autoMappedCount;
            }
            return configuredFieldCount;
        };

        const getDefaultFieldCount = () => {
            if (!configuredCrm) return 0;
            if (integration.name === 'salesforce' && configuredCrm?.object_mappings) {
                const objectMappings = configuredCrm.object_mappings || {};
                const keys = getSalesforceObjectKeys();
                return keys.reduce((sum: number, key: 'lead' | 'contact' | 'account') => {
                    const defaults = objectMappings?.[key]?.default_fields || {};
                    return sum + Object.keys(defaults).length;
                }, 0);
            }
            return Object.keys(configuredCrm.default_fields || {}).length;
        };

        return {
            configured: !!configuredCrm,
            mappedFields: getMappedFieldCount(),
            defaultFields: getDefaultFieldCount(),
        };
    };

    const userNameFor = (userId?: string | null) => {
        if (!userId) return null;
        const user = organizationUsers.find((u: any) => u.id === userId);
        if (!user) return null;
        return user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`
            : user.first_name || user.last_name || user.email || null;
    };

    const getConnectLink = (integrationName: string) => {
        if (typeof window === 'undefined') {
            return `/integrations?connect=${encodeURIComponent(integrationName)}`;
        }

        const params = new URLSearchParams({
            connect: integrationName,
            returnTo: `${window.location.pathname}${window.location.search}`
        });
        return `/integrations?${params.toString()}`;
    };

    const renderDisconnectedCard = (config: IntegrationCardConfig) => (
        <div key={config.name} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed p-4">
            <div className="flex min-w-0 items-center gap-3">
                <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center shrink-0 opacity-50">
                    <img src={config.icon} alt={`${config.displayName} Icon`} className={config.iconClassName || 'h-6'} />
                </div>
                <div>
                    <h4 className="text-sm font-medium text-muted-foreground">{config.displayName}</h4>
                    <p className="text-xs text-muted-foreground">Not connected</p>
                </div>
            </div>
            <Button variant="outline" size="sm" asChild>
                <Link to={getConnectLink(config.name)}>
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    Connect
                </Link>
            </Button>
        </div>
    );

    return (
        <div className="w-full">
            <AgentTabHeader
                icon={Zap}
                title="Actions"
                description="Connect integrations and configure automations your agent can trigger."
            />

            <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
            {/* CRM Section */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">CRM</h3>
                    <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">sync</Badge>
                </div>

                {isLoading ? (
                    <LoadingSkeleton />
                ) : (
                    <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
                        {CRM_CARD_CONFIGS.map((config) => {
                            const integration = crmIntegrations.find((item) => item.name === config.name);
                            if (!integration) {
                                return renderDisconnectedCard(config);
                            }

                            const info = getCrmInfo(integration);
                            return (
                                <div
                                    key={integration.id}
                                    className={clsx(
                                        'group relative rounded-lg border bg-card p-4 transition-colors hover:border-foreground/20',
                                        info.configured && 'border-green-500/30 bg-green-500/[0.03]'
                                    )}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center shrink-0">
                                            <img src={config.icon} alt={`${config.displayName} Icon`} className={config.iconClassName || 'h-6'} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-sm font-medium truncate">{config.displayName}</h4>
                                                {info.configured && (
                                                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {info.configured ? (
                                                    <>
                                                        {info.mappedFields} field{info.mappedFields !== 1 ? 's' : ''} mapped
                                                        {info.defaultFields > 0 && `, ${info.defaultFields} defaults`}
                                                    </>
                                                ) : (
                                                    'Not configured'
                                                )}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                                onClick={() => handleCrmIntegrationClick(integration)}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            {info.configured && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                    onClick={() => handleRemoveCrmIntegration(integration)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Scheduling Section */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">Scheduling</h3>
                    <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">tool</Badge>
                </div>

                {isLoading ? (
                    <LoadingSkeleton />
                ) : (
                    <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
                        {SCHEDULING_CARD_CONFIGS.flatMap((config) => {
                            const matching = calendarIntegrations.filter((item) => item.name === config.name);
                            if (matching.length === 0) {
                                return [renderDisconnectedCard(config)];
                            }

                            return matching.map((integration) => {
                                const isActive = selectedCalendarIntegration === integration.id;
                                const linkLabel = formatCalendarOptionLabel({
                                    providerName: integration.name,
                                    userName: userNameFor(integration.user_id),
                                    details: integration.details,
                                });
                                return (
                                <div
                                    key={integration.id}
                                    className={clsx(
                                        'group relative rounded-lg border bg-card p-4 transition-colors hover:border-foreground/20',
                                        isActive && 'border-green-500/30 bg-green-500/[0.03]'
                                    )}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center shrink-0">
                                            <img src={config.icon} alt={`${config.displayName} Icon`} className={config.iconClassName || 'h-6'} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-sm font-medium truncate">{config.displayName}</h4>
                                                {isActive && (
                                                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-0.5 truncate" title={linkLabel}>
                                                {isActive ? `Active · ${linkLabel}` : linkLabel}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                                onClick={() => handleCardClick(integration)}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            {isActive && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                    onClick={() => handleRemoveScheduling(integration)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                );
                            });
                        })}
                    </div>
                )}
            </div>

            {/* Ads Section */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">Ads</h3>
                    <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">sync</Badge>
                </div>

                {isLoading ? (
                    <LoadingSkeleton />
                ) : (
                    <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
                        {ADS_CARD_CONFIGS.map((config) => {
                            const integration = googleAdsIntegration && googleAdsIntegration.name === config.name
                                ? googleAdsIntegration
                                : null;

                            if (!integration) {
                                return renderDisconnectedCard(config);
                            }

                            return (
                                <div
                                    key={integration.id}
                                    className={clsx(
                                        'group relative rounded-lg border bg-card p-4 transition-colors hover:border-foreground/20',
                                        actions.ads?.customer_id && 'border-green-500/30 bg-green-500/[0.03]'
                                    )}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center shrink-0">
                                            <img src={config.icon} alt={`${config.displayName} Icon`} className={config.iconClassName || 'h-6'} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-sm font-medium truncate">{config.displayName}</h4>
                                                {actions.ads?.customer_id && (
                                                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                                {actions.ads?.customer_id
                                                    ? `${googleAdsAccounts.find(acc => acc.id === actions.ads?.customer_id)?.name || actions.ads.customer_id}`
                                                    : 'Not configured'}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                                onClick={handleConfigureGoogleAds}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            {actions.ads?.customer_id && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                    onClick={handleRemoveGoogleAds}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Notifications Section */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">Notifications</h3>
                </div>

                {isLoading ? (
                    <LoadingSkeleton />
                ) : (
                    <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
                        {/* Escalations */}
                        <div
                            className={clsx(
                                'group relative rounded-lg border bg-card p-4 transition-colors hover:border-foreground/20',
                                actions.escalations && checkLicenseType('pro') && 'border-green-500/30 bg-green-500/[0.03]'
                            )}
                        >
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center shrink-0">
                                    <img src={logo} alt="Aimdoc Logo" className="h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-sm font-medium truncate">Escalations</h4>
                                        <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">tool</Badge>
                                        {!checkLicenseType('pro') && (
                                            <Badge variant="outline" className="text-[10px] text-muted-foreground">Pro</Badge>
                                        )}
                                        {actions.escalations && checkLicenseType('pro') && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {checkLicenseType('pro')
                                            ? (actions.escalations ? 'Configured' : 'Not configured')
                                            : 'Engage plan required'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className={clsx(
                                                        "h-8 w-8",
                                                        checkLicenseType('pro')
                                                            ? "text-muted-foreground hover:text-foreground"
                                                            : "text-muted-foreground/40 cursor-not-allowed"
                                                    )}
                                                    onClick={checkLicenseType('pro') ? () => handleAimdocNotificationClick('escalations') : undefined}
                                                    disabled={!checkLicenseType('pro')}
                                                >
                                                    {checkLicenseType('pro') ? <Pencil className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {checkLicenseType('pro') ? 'Configure escalations' : 'Engage plan only'}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    {actions.escalations && checkLicenseType('pro') && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                            onClick={() => handleRemoveAimdocNotification('escalations')}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Buyer Intent */}
                        <div
                            className={clsx(
                                'group relative rounded-lg border bg-card p-4 transition-colors hover:border-foreground/20',
                                actions.buyer_intent && checkLicenseType('business') && 'border-green-500/30 bg-green-500/[0.03]'
                            )}
                        >
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center shrink-0">
                                    <img src={logo} alt="Aimdoc Logo" className="h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-sm font-medium truncate">Buyer Intent</h4>
                                        <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">tool</Badge>
                                        {!checkLicenseType('business') && (
                                            <Badge variant="outline" className="text-[10px] text-muted-foreground">Business</Badge>
                                        )}
                                        {actions.buyer_intent && checkLicenseType('business') && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {checkLicenseType('business')
                                            ? (actions.buyer_intent ? 'Configured' : 'Not configured')
                                            : 'Convert plan required'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className={clsx(
                                                        "h-8 w-8",
                                                        checkLicenseType('business')
                                                            ? "text-muted-foreground hover:text-foreground"
                                                            : "text-muted-foreground/40 cursor-not-allowed"
                                                    )}
                                                    onClick={checkLicenseType('business') ? () => handleAimdocNotificationClick('buyer_intent') : undefined}
                                                    disabled={!checkLicenseType('business')}
                                                >
                                                    {checkLicenseType('business') ? <Pencil className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {checkLicenseType('business') ? 'Configure buyer intent' : 'Convert plan only'}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    {actions.buyer_intent && checkLicenseType('business') && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                            onClick={() => handleRemoveAimdocNotification('buyer_intent')}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Slack */}
                        {slackIntegration ? (
                            <div
                                className={clsx(
                                    'group relative rounded-lg border bg-card p-4 transition-colors hover:border-foreground/20',
                                    actions.slack && 'border-green-500/30 bg-green-500/[0.03]'
                                )}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center shrink-0">
                                        <img src={SlackIcon} alt="Slack Icon" className="h-6" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-sm font-medium truncate">Slack</h4>
                                            <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">sync</Badge>
                                            {actions.slack && (
                                                <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {actions.slack
                                                ? `${actions.slack.configs?.length || 0} channel${(actions.slack.configs?.length || 0) !== 1 ? 's' : ''}`
                                                : 'Not configured'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                            onClick={() => {
                                                setSelectedNotificationIntegration(slackIntegration);
                                                setIsSlackModalOpen(true);
                                            }}
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        {actions.slack && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                onClick={(e) => handleRemoveSlackConfig(e)}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed p-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center shrink-0 opacity-50">
                                        <img src={SlackIcon} alt="Slack Icon" className="h-6" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground">Slack</h4>
                                        <p className="text-xs text-muted-foreground">Not connected</p>
                                    </div>
                                </div>
                                <Button variant="outline" size="sm" asChild>
                                    <Link to={getConnectLink('slack')}>
                                        <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                        Connect
                                    </Link>
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modals */}
            <Dialog open={showDisconnectModal} onOpenChange={setShowDisconnectModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Action</DialogTitle>
                    </DialogHeader>
                    <div className="mt-2">
                        <p className="text-sm text-muted-foreground">
                            Are you sure you want to delete {integrationToDisconnect?.name}?
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDisconnectModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDisconnect}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <SchedulingFieldsModal
                isOpen={isFieldMappingOpen}
                closeModal={handleModalClose}
                formFields={formFields}
                onSave={handleFieldMappingSave}
                existingMappings={actions.scheduling?.fields || {}}
                existingValueMappings={actions.scheduling?.value_mappings || {}}
                integrationName={
                    SCHEDULING_CARD_CONFIGS.find((config) => config.name === pendingIntegration?.name)?.displayName
                    || pendingIntegration?.name
                    || ''
                }
                integrationId={pendingIntegration?.id}
            />

            <SlackNotificationsModal
                isOpen={isSlackModalOpen}
                closeModal={() => setIsSlackModalOpen(false)}
                integration={selectedNotificationIntegration}
                escalationEnabled={escalationEnabled}
                enableTracks={enableTracks}
                actions={actions}
                value={actions.slack}
                onSave={(value) => {
                    setActions({ ...actions, slack: value });
                    setNeedsUpdate(true);
                }}
            />

            <AimdocNotificationsModal
                isOpen={isAimdocModalOpen}
                closeModal={() => setIsAimdocModalOpen(false)}
                notificationType={selectedAimdocNotificationType}
                value={actions[selectedAimdocNotificationType]}
                onSave={handleAimdocNotificationSave}
            />

            <Dialog open={showSlackDisconnectModal} onOpenChange={setShowSlackDisconnectModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Action</DialogTitle>
                    </DialogHeader>
                    <div className="mt-2">
                        <p className="text-sm text-muted-foreground">
                            Are you sure you want to remove Slack notifications?
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSlackDisconnectModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleSlackDisconnect}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <HubspotFieldsModal
                isOpen={isHubspotModalOpen}
                closeModal={() => setIsHubspotModalOpen(false)}
                formFields={formFields}
                onSave={handleCrmMappingSave}
                existingMappings={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.fields || {}}
                defaultFields={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.default_fields || {}}
                existingValueMappings={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.value_mappings || {}}
                existingReadFields={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.read_fields || []}
                existingReadDeals={Boolean(actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.read_deals)}
                existingAiTools={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.ai_tools ?? null}
                existingActivityTypes={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.activity_types ?? null}
                integrationId={selectedCrmIntegration?.id || ''}
            />

            <SalesforceFieldsModal
                isOpen={isSalesforceModalOpen}
                closeModal={() => setIsSalesforceModalOpen(false)}
                formFields={formFields}
                onSave={handleSalesforceMappingSave}
                existingConfig={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id) || undefined}
                integrationId={selectedCrmIntegration?.id || ''}
            />

            <AttioFieldsModal
                isOpen={isAttioModalOpen}
                closeModal={() => setIsAttioModalOpen(false)}
                formFields={formFields}
                onSave={handleCrmMappingSave}
                existingMappings={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.fields || {}}
                defaultFields={actions.crm?.find((crm: any) => crm.id === selectedCrmIntegration?.id)?.default_fields || {}}
                integrationId={selectedCrmIntegration?.id || ''}
            />

            <Dialog open={isGoogleAdsModalOpen} onOpenChange={setIsGoogleAdsModalOpen}>
                <DialogContent className="sm:max-w-[525px]">
                    <DialogHeader>
                        <DialogTitle>Configure Google Ads Tracking</DialogTitle>
                        <DialogDescription>
                            Select the Google Ads Account for this agent.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4 space-y-6">
                        {isLoadingAdsAccounts ? (
                            <div className="flex justify-center items-center py-6">
                                <Loader2 className="h-6 w-6 animate-spin" />
                            </div>
                        ) : googleAdsError ? (
                            <p className="text-sm text-red-600 text-center py-4 whitespace-pre-line">{googleAdsError}</p>
                        ) : googleAdsAccounts.length > 0 ? (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="google-ads-account">Google Ads Account</Label>
                                    <Select
                                        value={selectedGoogleAdsAccount}
                                        onValueChange={handleGoogleAdsAccountSelect}
                                    >
                                        <SelectTrigger id="google-ads-account">
                                            <SelectValue placeholder="Select a Google Ads Account" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {googleAdsAccounts.map((account) => (
                                                <SelectItem key={account.id} value={account.id}>
                                                    {account.name} ({account.id})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="google-ads-conversion-value">Default Conversion Value (USD)</Label>
                                    <Input
                                        id="google-ads-conversion-value"
                                        type="number"
                                        value={googleAdsConversionValue}
                                        onChange={(e) => setGoogleAdsConversionValue(e.target.value)}
                                        placeholder="e.g., 1.00"
                                        min="0.01"
                                        step="0.01"
                                    />
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-center py-4">
                                No accessible accounts found. Please check permissions in Google Ads.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsGoogleAdsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleGoogleAdsSave}
                            disabled={isLoadingAdsAccounts || isSavingGoogleAdsConfig || !selectedGoogleAdsAccount || !!googleAdsError || !googleAdsConversionValue}
                        >
                            {isSavingGoogleAdsConfig ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Save Configuration
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            </div>
        </div>
    );
}
